'use client';

import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '../../components/button';
import { Field } from '../../components/field';
import { Text } from '../../components/text';
import { RoleDescriptionEditor } from './RoleDescriptionEditor';

export interface RoleFormProps {
  applicationId: string;
  role?: ApplicationRoleDto;
  hasGitIntegration?: boolean;
  onSave: (role: {
    name: string;
    description?: string;
    enableAiScanning?: boolean;
  }) => Promise<void>;
  onCancel: () => void;
  onRoleAnalyze?: (roleId: string) => Promise<{
    jobId: string;
  }>;
  isModal?: boolean;
}
export function RoleForm({
  applicationId: _applicationId,
  role,
  hasGitIntegration = false,
  onSave,
  onCancel,
  onRoleAnalyze,
  isModal = false,
}: RoleFormProps) {
  const t = useTranslations('designSystem.applicationRoles');
  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
  }>({});
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: {
      name?: string;
    } = {};

    if (!name.trim()) {
      newErrors.name = t('roleForm.roleNameRequired');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      return;
    }

    setIsSaving(true);

    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        enableAiScanning: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit}>
      <VStack alignItems="stretch" gap={4}>
        {!isModal && (
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text fontSize="lg" fontWeight="semibold" color="fg.default">
              {role ? t('editRole') : t('createRole')}
            </Text>
            <Button variant="ghost" size="xs" onClick={onCancel} type="button">
              <Cancel01Icon size={14} />
            </Button>
          </HStack>
        )}

        <Field.Root>
          <Field.Label>
            {t('roleForm.roleName')}{' '}
            <Text as="span" color="fg.error">
              *
            </Text>
          </Field.Label>
          <Field.Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) {
                setErrors({ ...errors, name: undefined });
              }
            }}
            placeholder={t('roleForm.roleNamePlaceholder')}
            required
          />
          {errors.name && <Field.ErrorText>{errors.name}</Field.ErrorText>}
        </Field.Root>

        <VStack alignItems="stretch" gap={3}>
          <RoleDescriptionEditor
            role={role}
            description={description}
            onDescriptionChange={setDescription}
            hasGitIntegration={hasGitIntegration}
            onRoleAnalyze={onRoleAnalyze}
          />
        </VStack>

        <HStack justifyContent="flex-end" gap={2} pt={2}>
          <Button variant="outline" onClick={onCancel} type="button">
            {t('roleForm.cancel')}
          </Button>
          <Button type="submit" loading={isSaving}>
            {role ? t('roleForm.update') : t('roleForm.create')}
          </Button>
        </HStack>
      </VStack>
    </form>
  );

  if (isModal) {
    return formContent;
  }

  return (
    <Box
      p={5}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.default"
      bg="bg.default"
    >
      {formContent}
    </Box>
  );
}
